import React, { useState, useRef, useEffect } from "react";
import { Send, Sparkles, AlertCircle, RefreshCw, User, GraduationCap } from "lucide-react";
import { motion } from "motion/react";
import DOMPurify from "dompurify";
import { api } from "../lib/api";

interface Message {
  role: "user" | "assistant";
  content: string;
}

interface GuruChatProps {
  activeTier?: "PRO" | "LIFETIME_FOUNDER" | "GENERAL";
}

const CONVERSATION_STARTERS = [
  {
    label: "Paniniya Shiksha",
    prompt: "What is Paniniya Shiksha and how does it classify Sanskrit sound articulation (Sthana & Karana)?"
  },
  {
    label: "Meaning of 'Na Mama'",
    prompt: "Can you explain the deep spiritual and philosophical meaning of 'Idam Agnaye Na Mama' recited during Hawan?"
  },
  {
    label: "Hora & Ritual Timing",
    prompt: "As an astrologer, how can I calculate or align my Vedic Hawan rituals with beneficial astrological planetary Horas?"
  },
  {
    label: "Sanskrit Pitch Accents",
    prompt: "Explain the three main pitch accents (Udatta, Anudatta, Svarita) used in Rigveda recitation."
  }
];

export default function GuruChat({ activeTier = "GENERAL" }: GuruChatProps) {
  const [messages, setMessages] = useState<Message[]>([
    {
      role: "assistant",
      content: "Hariḥ Oṃ! I am your Vedic Gurukul Acharya. I can guide you through Sanskrit phonetics (Shiksha), Visarga/Anusvara rules, Rigveda accents, and step-by-step Hawan fire procedures. How can I assist you on your spiritual path today?"
    }
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [queryCount, setQueryCount] = useState(() => {
    try {
      const saved = localStorage.getItem("guru_daily_query_count");
      return saved ? Number(saved) : 0;
    } catch (e) {
      return 0;
    }
  });

  const chatEndRef = useRef<HTMLDivElement | null>(null);

  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading]);

  const handleSendMessage = async (textToSend?: string) => {
    const rawText = (textToSend || input).trim();
    if (!rawText) return;

    // Sanitize user input via DOMPurify to prevent XSS injection
    const text = DOMPurify.sanitize(rawText);
    if (!text) return;

    if (!textToSend) setInput("");
    setErrorMessage(null);

    // Enforce 3 queries limit on Free (GENERAL) tier
    if (activeTier === "GENERAL" && queryCount >= 3) {
      setErrorMessage("Vedic Gurukul AI daily limit reached (3/3 queries). Unlock unlimited high-fidelity guidance by upgrading to PRO or Lifetime Founder in Ancient Runes Pro!");
      return;
    }

    const updatedMessages: Message[] = [...messages, { role: "user" as const, content: text }];
    setMessages(updatedMessages);
    setIsLoading(true);

    // Strip redundant initial assistant welcome messages to conserve input tokens
    const filteredForApi = updatedMessages.filter((m) => {
      if (m.role === "assistant" && (m.content.includes("Hariḥ Oṃ!") || m.content.includes("Vedic Gurukul Acharya"))) {
        return false;
      }
      return true;
    });

    // Prune history to last 4 turns to minimize input token footprint
    const prunedMessages = filteredForApi.length > 0 ? filteredForApi.slice(-4) : [{ role: "user", content: text }];

    try {
      const response = await api.api.chat.$post({
        json: {
          messages: prunedMessages
        }
      });

      const data = (await response.json()) as any;

      if (!response.ok) {
        if (data.error === "API_KEY_MISSING") {
          setErrorMessage("Workspace Gemini API key is missing. Please contact system administrator.");
        } else {
          setErrorMessage(data.message || "An error occurred while communicating with the guru.");
        }
        setIsLoading(false);
        return;
      }

      setMessages(prev => [...prev, { role: "assistant", content: data.response || data.text }]);
      
      // Update query count on success
      const nextQueryCount = queryCount + 1;
      setQueryCount(nextQueryCount);
      localStorage.setItem("guru_daily_query_count", String(nextQueryCount));
    } catch (err: any) {
      console.error("Chat Error:", err);
      setErrorMessage("Could not connect to the server. Make sure the server is fully started.");
    } finally {
      setIsLoading(false);
    }
  };

  const clearChat = () => {
    setMessages([
      {
        role: "assistant",
        content: "Hariḥ Oṃ! I am your Vedic Gurukul Acharya. How can I assist you in Sanskrit articulation or Hawan procedures today?"
      }
    ]);
    setErrorMessage(null);
  };

  return (
    <div className="bg-neutral-900 border border-neutral-800 rounded-2xl flex flex-col h-[520px] shadow-xl overflow-hidden" id="gurukul-chat-bot">
      {/* Header */}
      <div className="bg-neutral-950 border-b border-neutral-800 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-amber-500/10 text-amber-500 rounded-xl border border-amber-500/20">
            <GraduationCap className="w-5 h-5" />
          </div>
          <div>
            <h4 className="text-sm font-semibold text-neutral-100 flex items-center gap-1.5">
              Vedic Gurukul Assistant
              <span className="text-[9px] font-mono tracking-widest uppercase px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400">
                AI Acharya
              </span>
            </h4>
            <p className="text-[11px] text-neutral-500">Traditional Phonetic & Astrological Guidance</p>
          </div>
        </div>

        <button
          onClick={clearChat}
          className="text-xs text-neutral-400 hover:text-neutral-200 bg-neutral-800 border border-neutral-700 px-2.5 py-1.5 rounded-lg transition"
        >
          Reset Chat
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-neutral-950/20 custom-scrollbar">
        {messages.map((m, idx) => (
          <div
            key={idx}
            className={`flex gap-3 max-w-[85%] ${
              m.role === "user" ? "ml-auto flex-row-reverse" : "mr-auto"
            }`}
          >
            <div
              className={`w-7 h-7 rounded-full flex items-center justify-center text-xs flex-shrink-0 ${
                m.role === "user"
                  ? "bg-amber-500 text-neutral-950"
                  : "bg-neutral-800 text-amber-500 border border-neutral-700"
              }`}
            >
              {m.role === "user" ? <User className="w-4 h-4" /> : <GraduationCap className="w-4 h-4" />}
            </div>

            <div className="flex flex-col gap-1">
              <div
                className={`text-xs rounded-2xl px-4 py-2.5 leading-relaxed break-words shadow-sm whitespace-pre-wrap ${
                  m.role === "user"
                    ? "bg-amber-500 text-neutral-950 font-medium"
                    : "bg-neutral-900 border border-neutral-800 text-neutral-200"
                }`}
              >
                {m.content}
              </div>
            </div>
          </div>
        ))}

        {isLoading && (
          <div className="flex gap-3 mr-auto max-w-[85%] animate-pulse">
            <div className="w-7 h-7 rounded-full bg-neutral-800 border border-neutral-700 flex items-center justify-center text-amber-500 flex-shrink-0">
              <GraduationCap className="w-4 h-4" />
            </div>
            <div className="bg-neutral-900 border border-neutral-800 text-neutral-400 text-xs rounded-2xl px-4 py-2.5 flex items-center gap-1.5">
              <RefreshCw className="w-3.5 h-3.5 animate-spin text-amber-500" />
              Guruji is contemplating the Shastras...
            </div>
          </div>
        )}

        {errorMessage && (
          <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 text-red-400 text-xs rounded-xl p-3 mt-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <p className="flex-1">{errorMessage}</p>
          </div>
        )}

        <div ref={chatEndRef} />
      </div>

      {/* Quick starters */}
      {messages.length === 1 && (
        <div className="bg-neutral-950/60 border-t border-neutral-800 px-6 py-3.5">
          <p className="text-[10px] text-neutral-500 uppercase font-semibold tracking-wider mb-2">
            Suggested Vedic Discussions:
          </p>
          <div className="flex flex-wrap gap-2">
            {CONVERSATION_STARTERS.map((s, idx) => (
              <button
                key={idx}
                disabled={isLoading}
                onClick={() => handleSendMessage(s.prompt)}
                className="text-[11px] bg-neutral-900 border border-neutral-800 hover:border-amber-500/40 text-neutral-300 hover:text-amber-400 px-3 py-1.5 rounded-lg transition cursor-pointer"
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input Form */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSendMessage();
        }}
        className="bg-neutral-950 border-t border-neutral-800 p-4 flex gap-2"
      >
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={isLoading ? "Please wait..." : "Ask the Acharya (e.g., 'What is Paniniya Shiksha?')..."}
          disabled={isLoading}
          className="flex-1 bg-neutral-900 border border-neutral-800 text-xs text-neutral-200 placeholder-neutral-500 rounded-xl px-4 py-2.5 outline-none focus:border-amber-500 transition-colors"
        />
        <button
          type="submit"
          disabled={isLoading || !input.trim()}
          className="p-2.5 bg-amber-500 disabled:opacity-40 hover:bg-amber-600 text-neutral-950 rounded-xl transition font-medium flex items-center justify-center"
        >
          <Send className="w-4 h-4" />
        </button>
      </form>
    </div>
  );
}
